import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Trophy, Star, Crown, Gem, Check } from 'lucide-react';

export default function TierInfo() {
  const tiers = [
    {
      name: 'Silver',
      icon: Star,
      color: 'text-gray-600',
      bgColor: 'bg-gray-100',
      revenue: 'Up to ₹75,000/month',
      commission: '15%',
      retainer: null,
      tenure: 'Immediate (starting tier)',
      benefits: [
        'Starting 15% commission rate',
        'Priority support',
        'Quarterly business reviews',
        'Early access to new products',
        'Partner dashboard access'
      ]
    },
    {
      name: 'Gold',
      icon: Crown,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
      revenue: '₹75,000 - ₹5,00,000/month',
      commission: '20%',
      retainer: '₹10,000/month',
      tenure: '1 year minimum',
      benefits: [
        'Premium 20% commission rate',
        '₹10,000 monthly retainer',
        'Dedicated account manager',
        'Custom marketing materials',
        'Equity-linked instrument (10% of 24-month revenue)',
        'Invitation to annual partner summit'
      ],
      highlight: true
    },
    {
      name: 'Platinum',
      icon: Gem,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      revenue: '₹5,00,001+/month',
      commission: '25%',
      retainer: '₹25,000/month',
      tenure: '2 years minimum',
      benefits: [
        'Elite 25% commission rate',
        '₹25,000 monthly retainer',
        'Executive partnership team',
        'Co-marketing opportunities',
        'Equity-linked instrument (10% of 24-month revenue)',
        'Strategic business planning sessions',
        'VIP event invitations'
      ],
      highlight: true
    }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Partnership Tiers
        </h1>
        <p className="text-muted-foreground text-lg">
          Grow your revenue and unlock higher commissions, retainers, and exclusive benefits
        </p>
      </div>

      {/* Tier Cards */}
      <div className="grid gap-6 md:grid-cols-2">
        {tiers.map((tier) => {
          const Icon = tier.icon;
          return (
            <Card 
              key={tier.name} 
              className={`hover:shadow-lg transition-all duration-200 ${tier.highlight ? 'border-2 border-primary' : ''}`}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-3">
                    <div className={`p-3 rounded-lg ${tier.bgColor}`}>
                      <Icon className={`h-6 w-6 ${tier.color}`} />
                    </div>
                    <span>{tier.name}</span>
                  </CardTitle>
                  {tier.highlight && (
                    <span className="px-3 py-1 bg-primary/10 text-primary text-xs font-semibold rounded-full">
                      EQUITY ELIGIBLE
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 pb-4 border-b border-border">
                  <div>
                    <p className="text-xs text-muted-foreground">Revenue Target</p>
                    <p className="font-semibold text-sm mt-1">{tier.revenue}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Min. Tenure</p>
                    <p className="font-semibold text-sm mt-1">{tier.tenure}</p>
                  </div>
                </div>
                
                <div className={`grid gap-4 pb-4 border-b border-border ${tier.retainer ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  <div>
                    <p className="text-xs text-muted-foreground">Commission</p>
                    <p className="text-2xl font-bold text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {tier.commission}
                    </p>
                  </div>
                  {tier.retainer && (
                    <div>
                      <p className="text-xs text-muted-foreground">Monthly Retainer</p>
                      <p className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {tier.retainer}
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <p className="text-sm font-semibold mb-3">Benefits</p>
                  <ul className="space-y-2">
                    {tier.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Additional Info */}
      <Card>
        <CardHeader>
          <CardTitle>How Tier Progression Works</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold mb-2">📊 Revenue-Based Advancement</h3>
              <p className="text-sm text-muted-foreground">
                Your tier is determined by your monthly revenue generation. Meet the revenue threshold and tenure requirement to advance to the next tier.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">⏱️ Tenure Requirements</h3>
              <p className="text-sm text-muted-foreground">
                Higher tiers require longer partnership commitment. This ensures sustained performance and mutual growth.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">💰 Automatic Commission Adjustment</h3>
              <p className="text-sm text-muted-foreground">
                As you advance tiers, your commission rate automatically increases on all future transactions. Past transactions maintain their original rate.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">🎯 Equity Participation</h3>
              <p className="text-sm text-muted-foreground">
                Gold and Platinum partners become eligible for equity-linked instruments valued at 10% of their 24-month revenue, based on our last fundraising valuation.
              </p>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <p className="text-sm font-medium mb-2">Starting Benefits</p>
            <p className="text-sm text-muted-foreground">
              All new partners start at Silver tier with 15% commission. As you grow your revenue to ₹75,000/month, you'll advance to Gold tier and unlock the monthly retainer, equity participation, and higher commissions.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
